import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';
const SUPPORTED_LANGUAGES = ['ja', 'en'];
const DEFAULT_LANGUAGE = 'en';
export const useLanguageStore = create()(devtools(persist((set, get) => ({
    language: DEFAULT_LANGUAGE,
    setLanguage: (lang) => {
        set({ language: lang });
        // Don't automatically redirect - let the components handle navigation
    },
    detectLanguageFromURL: () => {
        const path = window.location.pathname;
        // Check URL path: /ja/... or /en/...
        const pathMatch = path.match(/^\/([a-z]{2})(\/|$)/);
        if (pathMatch && SUPPORTED_LANGUAGES.includes(pathMatch[1])) {
            return pathMatch[1];
        }
        // Default to Japanese if no language prefix
        return DEFAULT_LANGUAGE;
    },
    detectLanguageFromBrowser: () => {
        const browserLang = navigator.language.toLowerCase();
        // Check exact match
        if (SUPPORTED_LANGUAGES.includes(browserLang)) {
            return browserLang;
        }
        // Check language code (first two characters)
        const langCode = browserLang.substring(0, 2);
        if (SUPPORTED_LANGUAGES.includes(langCode)) {
            return langCode;
        }
        // Check navigator.languages array
        for (const lang of navigator.languages || []) {
            const code = lang.toLowerCase().substring(0, 2);
            if (SUPPORTED_LANGUAGES.includes(code)) {
                return code;
            }
        }
        return DEFAULT_LANGUAGE;
    },
    initializeLanguage: () => {
        const state = get();
        const path = window.location.pathname;
        // Priority: URL > localStorage > Browser > Default
        const urlLang = state.detectLanguageFromURL();
        // Check if we're at root or don't have language in path
        const hasLangInPath = /^\/[a-z]{2}(\/|$)/.test(path);
        if (!hasLangInPath) {
            // No language in path - determine language from localStorage or browser
            // Priority: localStorage > Browser > Default
            const storedLang = localStorage.getItem('darkchat-language');
            let detectedLang = DEFAULT_LANGUAGE;
            if (storedLang) {
                try {
                    const parsed = JSON.parse(storedLang);
                    if (parsed.state?.language && SUPPORTED_LANGUAGES.includes(parsed.state.language)) {
                        detectedLang = parsed.state.language;
                    }
                }
                catch { }
            }
            else {
                detectedLang = state.detectLanguageFromBrowser();
            }
            // Set language without redirecting
            set({ language: detectedLang });
        }
        else {
            // Language is in path, use it
            set({ language: urlLang });
        }
    }
}), {
    name: 'darkchat-language',
    partialize: (state) => ({ language: state.language })
}), {
    name: 'language-store',
}));
// Helper hook for translations
export function useTranslation() {
    const language = useLanguageStore((state) => state.language);
    return {
        t: (key, translations) => {
            return translations[language] || translations[DEFAULT_LANGUAGE] || key;
        },
        language
    };
}
